import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, Download, Expand, Image as ImageIcon, Loader2, Upload, WandSparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { uploadFile } from '@/shared/api/app-client'
import type { ApiResponse, AssetUploadResult } from '@/shared/api/app-types'
import {
  ASPECT_RATIO_OPTIONS,
  IMAGE_MODEL_OPTIONS,
  RESOLUTION_OPTIONS,
  TIER_LABELS,
  aliyunImageService,
  getImageModelOption,
  type AspectRatio,
  type ImageModelTier,
  type ResolutionTier,
} from '@/features/image-gen/services/aliyun-image-service'

export function AiImageGenCanvas() {
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState(
    '一只橘色的猫坐在窗台上，窗外是下雨的城市夜景，霓虹灯光映在玻璃上，电影感构图，高细节',
  )
  const [negativePrompt, setNegativePrompt] = useState('')
  const [model, setModel] = useState('z-image-turbo')
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1')
  const [resolution, setResolution] = useState<ResolutionTier>('standard')
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savedAsset, setSavedAsset] = useState<AssetUploadResult | null>(null)
  const [result, setResult] = useState<{
    file: File
    objectUrl: string
  } | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const selectedModel = useMemo(() => getImageModelOption(model), [model])
  const canGenerate = prompt.trim().length > 0 && !isGenerating && !!selectedModel
  const canSave = !!result && !isSaving

  // 模型按档位分组
  const groupedModels = useMemo(() => {
    const groups: Record<ImageModelTier, typeof IMAGE_MODEL_OPTIONS[number][]> = {
      economy: [],
      balanced: [],
      quality: [],
      flagship: [],
    }
    for (const option of IMAGE_MODEL_OPTIONS) {
      groups[option.tier].push(option)
    }
    return groups
  }, [])

  const handleGenerate = async () => {
    if (!canGenerate || !selectedModel) return

    setIsGenerating(true)
    setError(null)
    setProgress(null)
    setSavedAsset(null)

    try {
      if (result) {
        URL.revokeObjectURL(result.objectUrl)
      }

      const generated = await aliyunImageService.generateImage({
        model: selectedModel.value,
        prompt,
        negativePrompt: selectedModel.supportsNegativePrompt ? negativePrompt : undefined,
        aspectRatio,
        resolution,
        onProgress: setProgress,
      })

      setResult({
        file: generated.file,
        objectUrl: generated.objectUrl,
      })
      setProgress('生成完成，可以下载或保存到素材库。')
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : 'AI 生图失败。')
      setProgress(null)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDownload = () => {
    if (!result) return

    const anchor = document.createElement('a')
    anchor.href = result.objectUrl
    anchor.download = result.file.name
    anchor.click()
  }

  const handleSaveAsset = async () => {
    if (!result || isSaving) return

    setIsSaving(true)
    setError(null)

    try {
      const uploadRes = await uploadFile<ApiResponse<AssetUploadResult>>(
        '/api/assets/upload',
        result.file,
      )
      setSavedAsset(uploadRes.data)
      setProgress('已保存到素材库。')
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '保存到素材库失败。')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <Link
          to="/app"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-accent"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">AI 生图</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            输入提示词，选择模型和比例，生成可下载或沉淀到素材库的图片。
          </p>
        </div>
      </div>

      <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
        {/* Left: generation settings */}
        <section className="space-y-6">
          {/* Prompt */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="image-prompt" className="text-sm font-medium">
                图片描述
              </Label>
              <span className="text-xs text-muted-foreground">{prompt.trim().length} 字</span>
            </div>
            <Textarea
              id="image-prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="描述你想生成的图片，例如：一只橘色的猫坐在窗台上，窗外是下雨的城市夜景…"
              className="min-h-[140px] resize-y"
            />
          </div>

          {/* Negative prompt (only for models that support it) */}
          {selectedModel?.supportsNegativePrompt ? (
            <div className="space-y-3">
              <Label htmlFor="image-negative-prompt" className="text-sm font-medium">
                负面提示词
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">（可选）</span>
              </Label>
              <Input
                id="image-negative-prompt"
                value={negativePrompt}
                onChange={(event) => setNegativePrompt(event.target.value)}
                placeholder="不想出现的内容，如：模糊、变形、低质量"
              />
            </div>
          ) : null}

          {/* Model */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">模型</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger>
                <SelectValue placeholder="选择模型" />
              </SelectTrigger>
              <SelectContent>
                {(['flagship', 'quality', 'balanced', 'economy'] as ImageModelTier[]).map((tier) => {
                  const options = groupedModels[tier]
                  if (!options || options.length === 0) return null
                  return (
                    <SelectGroup key={tier}>
                      <SelectLabel>{TIER_LABELS[tier]}</SelectLabel>
                      {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label} · {option.price}元/张
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )
                })}
              </SelectContent>
            </Select>
            {selectedModel ? (
              <p className="text-xs leading-5 text-muted-foreground">
                {selectedModel.description}
                {selectedModel.isAsync ? ' · 异步生成，耗时较长。' : ''}
              </p>
            ) : null}
          </div>

          {/* Aspect ratio + resolution */}
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-3">
              <Label className="text-sm font-medium">比例</Label>
              <Select
                value={aspectRatio}
                onValueChange={(value) => setAspectRatio(value as AspectRatio)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择比例" />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_RATIO_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">分辨率</Label>
              <Select
                value={resolution}
                onValueChange={(value) => setResolution(value as ResolutionTier)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择分辨率" />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label} · {option.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Generate (primary action stays with the settings) */}
          <Button onClick={() => void handleGenerate()} disabled={!canGenerate} className="w-full sm:w-auto">
            {isGenerating ? <Loader2 className="size-4 animate-spin" /> : <WandSparkles className="size-4" />}
            生成图片
          </Button>

          {progress ? (
            <div className="rounded-lg border border-primary/15 bg-primary/5 px-3.5 py-2.5 text-sm text-primary">
              {progress}
            </div>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </section>

        {/* Right: preview */}
        <section className="space-y-5">
          {result ? (
            <>
              <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {selectedModel?.label ?? model}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {aspectRatio} · {resolution === 'hd' ? '高清' : '标准'} ·{' '}
                      {Math.round(result.file.size / 1024)} KB
                    </div>
                  </div>
                  <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                    <ImageIcon className="size-5" />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="group/img relative block w-full"
                  title="点击查看大图"
                >
                  <img
                    src={result.objectUrl}
                    alt={prompt.slice(0, 60)}
                    className="w-full rounded-lg border border-border"
                  />
                  <span className="absolute right-2 top-2 flex size-8 items-center justify-center rounded-full bg-black/55 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover/img:opacity-100">
                    <Expand className="size-4" />
                  </span>
                </button>
              </div>

              {/* Result actions */}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleDownload} disabled={!result}>
                  <Download className="size-4" />
                  下载图片
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => void handleSaveAsset()}
                  disabled={!canSave}
                >
                  {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  保存到素材库
                </Button>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/app/assets', search: { tab: 'image' } })}
                >
                  去素材库查看
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/projects/new' })}
                >
                  新建项目并进入编辑器
                </Button>
              </div>

              {savedAsset ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3.5 py-2.5 text-sm text-emerald-700">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="size-4" />
                    已保存到素材库
                  </div>
                  <div className="mt-1 text-xs text-emerald-700/80">
                    素材 ID: {savedAsset.id} · 文件名: {savedAsset.fileName}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-border px-8 text-center">
              {isGenerating ? (
                <Loader2 className="size-8 animate-spin text-muted-foreground/50" />
              ) : (
                <ImageIcon className="size-8 text-muted-foreground/50" />
              )}
              <h3 className="mt-4 text-sm font-medium text-foreground">
                {isGenerating ? '生成中，请稍候…' : '生成结果会在这里展示'}
              </h3>
              <p className="mt-1.5 max-w-xs text-xs leading-5 text-muted-foreground">
                {isGenerating
                  ? '异步模型可能需要 10-30 秒，请勿离开页面。'
                  : '在左侧填写描述并生成后，可在这里下载或保存到素材库。'}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* 大图预览弹窗 */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          hideCloseButton
          className="max-w-none gap-0 overflow-hidden border-none bg-black/90 p-0"
          style={{ width: '100vw', height: '100vh', maxWidth: '100vw', left: '0', top: '0', transform: 'none', translate: 'none' }}
        >
          <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-4 py-3">
            <span className="truncate text-sm font-medium text-white">
              {selectedModel?.label ?? model} · {aspectRatio}
            </span>
            <div className="flex items-center gap-1">
              {result && (
                <button
                  onClick={handleDownload}
                  className="flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                  title="下载"
                >
                  <Download className="size-4" />
                </button>
              )}
              <button
                onClick={() => setPreviewOpen(false)}
                className="flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                title="关闭"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
          {result ? (
            <button
              type="button"
              onClick={() => setPreviewOpen(false)}
              className="flex h-full w-full items-center justify-center"
              title="点击关闭"
            >
              <img
                src={result.objectUrl}
                alt={prompt.slice(0, 60)}
                className="max-h-[90vh] max-w-[92vw] rounded-lg object-contain"
              />
            </button>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
